import { EnvironmentProviders, NgZone, inject, provideEnvironmentInitializer } from '@angular/core';
import { NgOption, NgSelectComponent } from '@ng-select/ng-select';

// Shared across every ng-select instance; window-level, so a single tracker suffices.
let multiSelectModifierHeld = false;
let installed = false;

// CTRL on Windows/Linux, ⌘ on macOS.
function isMultiSelectModifier(event: KeyboardEvent): boolean {
  return event.key === 'Control' || event.key === 'Meta';
}

/**
 * Keeps any multi-select ng-select dropdown open while the multi-select modifier is held,
 * so several items can be picked in one go. Applies to EVERY ng-select in the application —
 * `NgSelectComponent.prototype.select` is wrapped once, so no per-component or per-template
 * wiring is needed. The template's own `[closeOnSelect]` value is only bypassed for the
 * duration of a modifier-held selection, never overwritten.
 */
export function provideNgSelectCtrlMultiSelect(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => installCtrlMultiSelect(inject(NgZone)));
}

function installCtrlMultiSelect(zone: NgZone): void {
  if (installed) {
    return;
  }
  installed = true;

  // Plain state tracking — no change detection needed, so stay outside the Angular zone.
  zone.runOutsideAngular(() => {
    window.addEventListener('keydown', event => {
      if (isMultiSelectModifier(event)) {
        multiSelectModifierHeld = true;
      }
    });
    window.addEventListener('keyup', event => {
      if (isMultiSelectModifier(event)) {
        multiSelectModifierHeld = false;
      }
    });
    // Guards the "held the modifier, switched window, never saw keyup" stuck state.
    window.addEventListener('blur', () => {
      multiSelectModifierHeld = false;
    });
  });

  const originalSelect = NgSelectComponent.prototype.select;
  NgSelectComponent.prototype.select = function (this: NgSelectComponent, item: NgOption): void {
    if (!this.multiple || !multiSelectModifierHeld) {
      originalSelect.call(this, item);
      return;
    }

    const restingCloseOnSelect = this.closeOnSelect;
    this.closeOnSelect = false;
    try {
      originalSelect.call(this, item);
    } finally {
      this.closeOnSelect = restingCloseOnSelect;
    }
  };
}
